import { Dropdown, EmptyStateProps, Icon, IconButton, Icons, Table } from '@silverfort/sf-components';
import { useServiceAccountContext } from '../../../../contexts/ServiceAccountContext';
import { useCallback, useEffect, useState } from 'react';
import { httpGet } from '../../../../../../../components/utils';
import { INFINITE_SCROLL_BATCH_SIZE } from '@silverfort-nodelibs/common/utils/consts';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import ResourcePickerFilter from '../../../../../../../components/Controls/ResourcePicker/ResourcePickerFilter';
import {
    NotParsedServiceAccountTuple,
    ServiceAccountTuple,
    ServiceAccountsTuplesFilters
} from './ServiceAccountsTuplesTypes';
import { LAST_SEEN_FILTER, PROTOCOL_FILTER } from './ServiceAccountsTuplesConsts';
import { COLUMNS, mapToParsedTupleFilters } from './ServiceAccountsTuplesUtils';

const ServiceAccountsTuples = ({
    tableBodyHeight,
    emptyState
}: {
    tableBodyHeight: number;
    emptyState: EmptyStateProps;
}) => {
    const { updatedServiceAccount } = useServiceAccountContext();
    const [showInfoBanner, setShowInfoBanner] = useState<boolean>(true);
    const [serviceAccountsTuples, setServiceAccountsTuples] = useState<ServiceAccountTuple[]>([]);
    const [isFetchingTuples, setIsFetchingTuples] = useState<boolean>(false);
    const [sort, setSort] = useState<{ orderByField: keyof ServiceAccountTuple; orderDirection: 'asc' | 'desc' }>({
        orderByField: 'lastUse',
        orderDirection: 'desc'
    });
    const [lastFetchLength, setLastFetchLength] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [filters, setFilters] = useState<ServiceAccountsTuplesFilters>({});

    const fetchServiceAccountsTuples = useCallback(async () => {
        try {
            if (updatedServiceAccount?.guid) {
                setIsFetchingTuples(true);

                const response: {
                    data: {
                        tuples: NotParsedServiceAccountTuple[];
                    } | null;
                } = await httpGet(`/ServiceAccountsV2/${updatedServiceAccount.guid}/tuples`, {
                    [HTTP_OPTIONS.REQ_OPTIONS]: {
                        params: {
                            sort,
                            offset,
                            filters: mapToParsedTupleFilters(filters)
                        }
                    }
                });
                const newTuples =
                    response?.data?.tuples.map((tuple) => ({
                        ...tuple,
                        firstUse: new Date(tuple.firstUse),
                        lastUse: new Date(tuple.lastUse)
                    })) || [];
                setLastFetchLength(newTuples.length);
                setServiceAccountsTuples((prevTuples) => [...prevTuples, ...newTuples]);
            }
        } catch (error) {
            console.error('Error fetching service account tuples:', error);
        } finally {
            setIsFetchingTuples(false);
        }
    }, [updatedServiceAccount?.guid, sort, offset, filters]);
    useEffect(() => {
        fetchServiceAccountsTuples();
    }, [fetchServiceAccountsTuples]);

    const onSortChange = useCallback((columnKey: keyof ServiceAccountTuple, sortDirection: 'asc' | 'desc') => {
        setSort({ orderByField: columnKey, orderDirection: sortDirection });
        setOffset(0);
        setServiceAccountsTuples([]);
    }, []);

    const onFiltersChange = useCallback((partialFilters: Partial<ServiceAccountsTuplesFilters>) => {
        setFilters((prevFilters) => ({ ...prevFilters, ...partialFilters }));
        setOffset(0);
        setServiceAccountsTuples([]);
    }, []);

    const onScrollEnd = useCallback(() => {
        if (lastFetchLength < INFINITE_SCROLL_BATCH_SIZE) {
            return;
        }
        setOffset((offset) => offset + INFINITE_SCROLL_BATCH_SIZE);
    }, [lastFetchLength]);

    return (
        <div className="flex flex-col gap-4">
            {showInfoBanner && (
                <div className="flex flex-row w-full border border-[#188DED] rounded-[12px] bg-[#E9F5FF] p-4 mb-2 text-[#333] font-[Inter] text-[13px] font-normal leading-normal">
                    <Icon icon={Icons.CircleInfo} className="text-[#188DED] w-10 pr-3" />
                    The destination, source, protocol, and similar details in the Authentication tab are collected
                    starting from version 6.1. This means the data shown here may not match information in other tabs,
                    which was collected before that version.
                    <IconButton
                        className="w-4 h-4"
                        iconClassName="text-[#808080]"
                        icon={Icons.Cancel}
                        onClick={() => setShowInfoBanner(false)}
                    />
                </div>
            )}
            <div className="flex flex-wrap gap-2">
                <ResourcePickerFilter
                    filterTitle="Source"
                    chosenIps={filters.sources?.ips}
                    chosenResources={filters.sources?.resources}
                    onApplyFilter={(ips, resources) => onFiltersChange({ sources: { ips, resources } })}
                    onRemoveFilter={() => onFiltersChange({ sources: undefined })}
                />
                <ResourcePickerFilter
                    filterTitle="Destination"
                    chosenIps={filters.destinations?.ips}
                    chosenResources={filters.destinations?.resources}
                    onApplyFilter={(ips, resources) => onFiltersChange({ destinations: { ips, resources } })}
                    onRemoveFilter={() => onFiltersChange({ destinations: undefined })}
                />
                <Dropdown
                    {...PROTOCOL_FILTER}
                    onChange={(protocols) => onFiltersChange({ protocols: protocols as string[] })}
                    onRemove={() => onFiltersChange({ protocols: undefined })}
                    selectedItems={filters.protocols}
                    removable={filters.protocols?.length !== undefined && filters.protocols?.length > 0}
                />
                <Dropdown
                    {...LAST_SEEN_FILTER}
                    onChange={(lastUsedDays) => onFiltersChange({ lastUsedDays: lastUsedDays as number })}
                    onRemove={() => onFiltersChange({ lastUsedDays: undefined })}
                    selectedItems={filters.lastUsedDays ? [filters.lastUsedDays] : []}
                    removable={filters.lastUsedDays !== undefined}
                />
            </div>
            <div className="w-full overflow-x-auto">
                <Table
                    columns={COLUMNS}
                    rows={serviceAccountsTuples}
                    loading={isFetchingTuples}
                    defaultSortColumn={sort.orderByField}
                    defaultSortDirection={sort.orderDirection}
                    onSortChange={onSortChange}
                    tableContainerClassName="w-[calc(90vw)]"
                    bodyHeight={tableBodyHeight - 56}
                    EmptyStateProps={emptyState}
                    onScrollEnd={onScrollEnd}
                    isHorizontalScrollAvailable={true}
                />
            </div>
        </div>
    );
};

export default ServiceAccountsTuples;
